import React, { useState } from 'react';
import styled from 'styled-components';
import { FaTimes, FaCreditCard } from 'react-icons/fa';
import Input from '../../components/shared/Input';
import Select from '../../components/shared/Select';
import Button from '../../components/shared/Button';
import TextArea from '../../components/shared/TextArea';
import { recordPayment } from './financialService';
import { formatCurrency } from '../../utils/utils';

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
`;

const ModalTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${props => props.theme.colors.inputGray};
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${props => props.theme.colors.inputGray}; color: ${props => props.theme.colors.textPrimary}; }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const InfoBanner = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: 14px 16px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const InfoLabel = styled.span`
  font-size: 13px;
  color: ${props => props.theme.colors.textSecondary};
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.color || props.theme.colors.textPrimary};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.cols || '1fr'};
  gap: 16px;
  margin-bottom: 4px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: ${props => props.theme.borderRadius};
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${props => props.theme.colors.inputGray};
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CancelBtn = styled.button`
  padding: 12px 20px;
  border-radius: ${props => props.theme.borderRadius};
  border: 1px solid ${props => props.theme.colors.inputGray};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${props => props.theme.colors.background}; }
`;

const PaymentForm = ({ invoiceId, invoice, onClose, onSuccess }) => {
  const remaining = parseFloat(invoice.totalAmount) - parseFloat(invoice.paidAmount);

  // Find next unpaid installment for prefill
  let prefillAmount = remaining;
  let prefillLabel = '';
  let runningPaid = parseFloat(invoice.paidAmount);
  for (const item of (invoice.paymentSchedule || [])) {
    if (runningPaid >= item.amount) {
      runningPaid -= item.amount;
    } else {
      prefillAmount = item.amount - runningPaid;
      prefillLabel = item.label;
      break;
    }
  }

  const [formData, setFormData] = useState({
    amount: prefillAmount.toFixed(2),
    method: 'Check',
    referenceNumber: '',
    paymentDate: new Date().toISOString().split('T')[0],
    installmentLabel: prefillLabel,
    notes: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      await recordPayment(invoiceId, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalTop>
          <ModalTitle><FaCreditCard /> Record Payment</ModalTitle>
          <CloseBtn onClick={onClose}><FaTimes /></CloseBtn>
        </ModalTop>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <InfoBanner>
              <div>
                <InfoLabel>Invoice</InfoLabel>
                <div><InfoValue>{invoice.invoiceNumber}</InfoValue></div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <InfoLabel>Remaining Balance</InfoLabel>
                <div><InfoValue color="#ff9500">{formatCurrency(remaining)}</InfoValue></div>
              </div>
            </InfoBanner>

            {error && <ErrorBanner>{error}</ErrorBanner>}

            <FormRow cols="1fr 1fr">
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select
                  label="Method"
                  value={formData.method}
                  onChange={(e) => handleChange('method', e.target.value)}
                >
                  <option value="Check">Check</option>
                  <option value="Venmo">Venmo</option>
                  <option value="Cash">Cash</option>
                  <option value="Financing">Financing</option>
                  <option value="Other">Other</option>
                </Select>
              </div>
            </FormRow>

            <FormRow cols="1fr 1fr">
              <div>
                <Label>Reference #</Label>
                <Input
                  value={formData.referenceNumber}
                  onChange={(e) => handleChange('referenceNumber', e.target.value)}
                  placeholder="Check #, confirmation..."
                />
              </div>
              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => handleChange('paymentDate', e.target.value)}
                  required
                />
              </div>
            </FormRow>

            <div>
              <Label>Payment For</Label>
              <Select
                label="Installment"
                value={formData.installmentLabel}
                onChange={(e) => handleChange('installmentLabel', e.target.value)}
              >
                <option value="">Select...</option>
                {(invoice.paymentSchedule || []).map((s, i) => (
                  <option key={i} value={s.label}>{s.label} ({formatCurrency(s.amount)})</option>
                ))}
                <option value="Other">Other</option>
              </Select>
            </div>

            <div>
              <Label>Notes</Label>
              <TextArea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any additional notes..."
              />
            </div>
          </ModalBody>

          <ModalFooter>
            <CancelBtn type="button" onClick={onClose}>Cancel</CancelBtn>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Recording...' : 'Record Payment'}
            </Button>
          </ModalFooter>
        </form>
      </ModalCard>
    </Overlay>
  );
};

export default PaymentForm;
